// Phase 10b — operator-only admin surface for cancellation_policy.
//
// Conventions (mirrors 8c rate-admin):
//   - Money/percentage fields use STRING (regex-validated decimal) to
//     avoid JSON precision loss.
//   - Dates use 'YYYY-MM-DD' for effective_from / effective_to (column
//     type @db.Date in Prisma) and full ISO-8601 for timestamps.
//   - Every mutation request carries `reason` (operator must document
//     why) — feeds into cancellation_policy_change_log.
//   - 14-day lead time enforced server-side (10b-2 lock). Operator
//     override via { overrideLeadTime: true, overrideReason: ">=10
//     chars" }; the override + reason are persisted in the change
//     log's dedicated `lead_time_override_reason` column.
import { z } from 'zod';
import { BuyerTypeSchema } from './enums.schema.js';
// ===== shared pieces =====
const IsoDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD');
const ChargePctSchema = z
    .string()
    .regex(/^\d{1,3}(\.\d{1,2})?$/, 'chargePct must be a decimal 0..100 with up to 2 dp')
    .refine((s) => {
    const n = Number.parseFloat(s);
    return n >= 0 && n <= 100;
}, 'chargePct must be between 0 and 100');
const HoursThresholdSchema = z.number().int().positive().max(168); // ≤ 1 week
const ReasonSchema = z
    .string()
    .min(10, 'Reason must be at least 10 characters — document why')
    .max(500);
const LeadTimeOverrideSchema = z
    .object({
    overrideLeadTime: z.boolean().optional(),
    overrideReason: z.string().min(10).max(500).optional(),
})
    .refine((v) => !v.overrideLeadTime || (v.overrideReason !== undefined && v.overrideReason.length >= 10), { message: 'overrideReason is required (>=10 chars) when overrideLeadTime is true' });
// ===== cancellation-policy admin =====
export const AdminCancellationPolicyCreateRequestSchema = z
    .object({
    buyerType: BuyerTypeSchema,
    hoursBeforeThreshold: HoursThresholdSchema,
    chargePct: ChargePctSchema,
    effectiveFrom: IsoDateSchema,
    reason: ReasonSchema,
})
    .and(LeadTimeOverrideSchema);
export const AdminCancellationPolicySupersedeRequestSchema = z
    .object({
    newChargePct: ChargePctSchema,
    effectiveFrom: IsoDateSchema,
    reason: ReasonSchema,
})
    .and(LeadTimeOverrideSchema);
export const AdminCancellationPolicyDeprecateRequestSchema = z
    .object({
    effectiveTo: IsoDateSchema,
    reason: ReasonSchema,
})
    .and(LeadTimeOverrideSchema);
// v0/rate-provenance Phase 4 — in-place edit of an UNUSED cancellation policy.
// VALUE field only (chargePct — matches what supersede changes); identity
// (buyerType, hoursBeforeThreshold) + effective dates stay frozen. No reason /
// lead-time / audit. Server-gated to inUse=false.
export const AdminCancellationPolicyEditRequestSchema = z.object({
    chargePct: ChargePctSchema,
});
export const AdminCancellationPolicyResponseSchema = z.object({
    id: z.string().uuid(),
    buyerType: BuyerTypeSchema,
    hoursBeforeThreshold: z.number().int().positive(),
    chargePct: z.string(),
    effectiveFrom: IsoDateSchema,
    effectiveTo: IsoDateSchema.nullable(),
    isCurrent: z.boolean(),
    // v0/rate-provenance — has this policy priced a shift (via an InvoiceLine)?
    // Drives Edit/Delete (unused) vs Supersede/Deprecate (used) in the panel.
    inUse: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const AdminCancellationPolicyListQuerySchema = z.object({
    buyerType: BuyerTypeSchema.optional(),
    includeHistorical: z.coerce.boolean().default(false),
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(100).default(20),
});
export const AdminCancellationPolicyListResponseSchema = z.object({
    items: z.array(AdminCancellationPolicyResponseSchema),
    totalCount: z.number().int().min(0),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
});
// ===== cancellation-policy change log (read-only) =====
export const AdminCancellationPolicyChangeActionSchema = z.enum([
    'CREATED',
    'SUPERSEDED',
    'DEPRECATED',
]);
export const AdminCancellationPolicyChangeLogEntrySchema = z.object({
    id: z.string().uuid(),
    actorUserId: z.string().uuid(),
    actorEmail: z.string(),
    ts: z.string(),
    policyId: z.string().uuid(),
    action: AdminCancellationPolicyChangeActionSchema,
    oldValue: z.unknown().nullable(),
    newValue: z.unknown().nullable(),
    reason: z.string(),
    leadTimeOverrideReason: z.string().nullable(),
    appliedEffectiveFrom: IsoDateSchema,
});
export const AdminCancellationPolicyChangeLogListQuerySchema = z.object({
    policyId: z.string().uuid().optional(),
    action: AdminCancellationPolicyChangeActionSchema.optional(),
    actorUserId: z.string().uuid().optional(),
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(100).default(50),
});
export const AdminCancellationPolicyChangeLogListResponseSchema = z.object({
    items: z.array(AdminCancellationPolicyChangeLogEntrySchema),
    totalCount: z.number().int().min(0),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
});
//# sourceMappingURL=cancellation-policy-admin.schema.js.map